import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Card,
  CardContent,
  Divider,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Edit as EditIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { agentAPI } from '../services/api';

const AgentsPage = () => {
  const { agent: currentAgent } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [formData, setFormData] = useState({
    mobile: '',
    status: 'active',
    role: 'agent',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const response = await agentAPI.getAgents();
      if (response.success) {
        setAgents(response.data);
      }
    } catch (err) {
      console.error('Error fetching agents:', err);
      setError(err.response?.data?.message || 'Failed to fetch agents');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (agent = null) => {
    if (agent) {
      setEditingAgent(agent);
      setFormData({
        mobile: agent.mobile || '',
        status: agent.status || 'active',
        role: agent.role || 'agent',
      });
    } else {
      setEditingAgent(null);
      setFormData({
        mobile: '',
        status: 'active',
        role: 'agent',
      });
    }
    setError('');
    setSuccess('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingAgent(null);
    setFormData({
      mobile: '',
      status: 'active',
      role: 'agent',
    });
    setError('');
    setSuccess('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError('');
      setSuccess('');

      const updateData = {};
      if (formData.mobile !== undefined) {
        updateData.mobile = formData.mobile.trim() || null;
      }
      if (formData.status !== undefined) {
        updateData.status = formData.status;
      }
      if (formData.role !== undefined) {
        updateData.role = formData.role;
      }

      const response = await agentAPI.updateAgent(editingAgent._id, updateData);
      if (response.success) {
        setSuccess('Agent updated successfully');
        fetchAgents();
        setTimeout(() => {
          handleCloseDialog();
        }, 1500);
      }
    } catch (err) {
      console.error('Error updating agent:', err);
      setError(err.response?.data?.message || 'Failed to update agent');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 2 } }}>
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center" 
        mb={3}
      >
        <Typography variant={isMobile ? "h5" : "h4"}>Agents</Typography>
      </Box>

      {error && !openDialog && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {agents.length === 0 ? (
        <Paper sx={{ p: { xs: 2, sm: 4 }, textAlign: 'center' }}>
          <PersonIcon sx={{ fontSize: { xs: 48, sm: 64 }, color: 'text.secondary', mb: 2 }} />
          <Typography variant={isMobile ? "body1" : "h6"} color="text.secondary">
            No agents found
          </Typography>
        </Paper>
      ) : isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {agents.map((agent) => (
            <Card key={agent._id} sx={{ width: '100%' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Box display="flex" alignItems="center" gap={1} flex={1}>
                    {agent.role === 'admin' && (
                      <AdminPanelSettingsIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                    )}
                    <Typography variant="h6" fontWeight="medium">
                      {agent.name}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(agent)}
                    color="primary"
                    title="Edit Agent"
                  >
                    <EditIcon />
                  </IconButton>
                </Box>

                <Stack spacing={1.5}>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2">{agent.email}</Typography>
                  </Box>

                  {agent.mobile && (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2">{agent.mobile}</Typography>
                    </Box>
                  )}

                  <Box display="flex" flexWrap="wrap" gap={1}>
                    <Chip
                      label={agent.role}
                      size="small"
                      color={agent.role === 'admin' ? 'primary' : 'default'}
                    />
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {agent.status === 'active' ? (
                        <>
                          <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                          <Typography variant="body2" color="success.main" fontSize="0.75rem">
                            Active
                          </Typography>
                        </>
                      ) : (
                        <>
                          <CancelIcon sx={{ fontSize: 16, color: 'error.main' }} />
                          <Typography variant="body2" color="error.main" fontSize="0.75rem">
                            Inactive
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                      Created At
                    </Typography>
                    <Typography variant="body2" fontSize="0.875rem">
                      {formatDate(agent.createdAt)}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        <TableContainer 
          component={Paper} 
          sx={{ 
            overflowX: 'auto',
            '& .MuiTableCell-root': {
              whiteSpace: 'nowrap',
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              padding: { xs: '8px', sm: '16px' },
            },
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 150 }}>Name</TableCell>
                <TableCell sx={{ minWidth: 200 }}>Email</TableCell>
                <TableCell sx={{ minWidth: 120 }}>Mobile</TableCell>
                <TableCell sx={{ minWidth: 100 }}>Role</TableCell>
                <TableCell sx={{ minWidth: 100 }}>Status</TableCell>
                <TableCell sx={{ minWidth: 140 }}>Created At</TableCell>
                <TableCell align="right" sx={{ minWidth: 80 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {agents.map((agent) => (
                <TableRow key={agent._id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {agent.role === 'admin' && (
                        <AdminPanelSettingsIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      )}
                      <Typography variant="body1" fontWeight="medium">
                        {agent.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2">{agent.email}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {agent.mobile ? (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">{agent.mobile}</Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={agent.role}
                      size="small"
                      color={agent.role === 'admin' ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {agent.status === 'active' ? (
                        <>
                          <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                          <Typography variant="body2" color="success.main">
                            Active
                          </Typography>
                        </>
                      ) : (
                        <>
                          <CancelIcon sx={{ fontSize: 18, color: 'error.main' }} />
                          <Typography variant="body2" color="error.main">
                            Inactive
                          </Typography>
                        </>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDate(agent.createdAt)}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(agent)}
                      color="primary"
                      title="Edit Agent"
                    >
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            m: { xs: 0, sm: 2 },
            maxHeight: { xs: '100%', sm: '90vh' },
          },
        }}
      >
        <DialogTitle>Edit Agent</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          <Box sx={{ mt: 2 }}>
            <TextField
              margin="dense"
              name="mobile"
              label="Mobile Number"
              type="tel"
              fullWidth
              variant="outlined"
              value={formData.mobile}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              helperText="Optional. Enter mobile number with country code (e.g., +1234567890)"
            />
            <FormControl fullWidth margin="dense" sx={{ mt: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                label="Status"
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="dense" sx={{ mt: 2 }}>
              <InputLabel>Role</InputLabel>
              <Select
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                label="Role"
                disabled={editingAgent?._id === currentAgent?.agentId}
              >
                <MenuItem value="agent">Agent</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
            {editingAgent?._id === currentAgent?.agentId && (
              <Alert severity="info" sx={{ mt: 2 }}>
                You cannot change your own role or deactivate your own account.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
            {submitting ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgentsPage;

